/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Mail, FileText, Calendar, AlertCircle, Users, Stamp, MessageSquare, Inbox,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/courrier-notarial',
  slug: 'courrier-notarial',
  breadcrumbLabel: 'Courrier notarial',
  readingTime: '6 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'Courrier notarial : décrypter votre lettre | HÉRITA',
  seoDescription:
    "Comprenez facilement un courrier notarial : demande de pièces, projet d'acte, convocation, relance. HÉRITA traduit la lettre de votre notaire en français clair.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre un courrier notarial',
    subtitle:
      "Recevoir une lettre de notaire intrigue souvent. HÉRITA vous aide à comprendre ce qu'on attend précisément de vous, ce que la lettre annonce, et les délais à respecter.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'un courrier notarial\u00A0?",
    paragraphs: [
      <>
        Un courrier notarial est une lettre — papier ou recommandée —
        rédigée et signée par un notaire dans le cadre d'un dossier qu'il
        instruit (succession, vente, donation, divorce, mise à jour d'un acte
        antérieur). Il peut prendre <strong className="font-semibold text-[#1A2530]">de très nombreuses formes</strong>{' '}
        selon ce qu'il vise à provoquer.
      </>,
      <>
        Le plus souvent, c'est <em>une demande de pièces</em>&nbsp;: le
        notaire vous demande de fournir un acte de naissance, un titre de
        propriété, un relevé bancaire, une attestation d'assurance-vie. La
        suite du dossier en dépend.
      </>,
      <>
        Cela peut aussi être l'<em>annonce d'un projet d'acte</em>&nbsp;:
        une donation-partage, une attestation immobilière ou un acte de
        notoriété en préparation. Le courrier vous transmet alors un
        document pour relecture, en vous indiquant la date prévue de
        signature.
      </>,
      <>
        Cela peut encore être une <em>convocation</em>&nbsp;: le notaire
        vous fixe rendez-vous, parfois en cabinet, parfois en visioconférence,
        pour signer un acte, recueillir vos déclarations ou organiser un
        échange entre cohéritiers.
      </>,
      <>
        Plus rarement, c'est une <em>relance</em>&nbsp;: un délai n'a pas été
        tenu, une pièce manque, un acte stagne. Le courrier rappelle les
        échéances et leurs conséquences (pénalités, blocage du dossier,
        intérêts de retard).
      </>,
      <>
        Comprendre une lettre de notaire, c'est savoir <em>exactement</em> ce
        qu'elle attend de vous et dans quel délai. HÉRITA résume la demande
        en français clair, liste les pièces à fournir et fait le lien avec
        le dossier en cours — qu'il s'agisse d'une succession, d'une
        donation-partage ou d'un{' '}
        <Link to="/acte-de-vente" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">acte de vente</Link>.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert un courrier notarial\u00A0?",
    items: [
      { title: 'Vous informer', description: "Le notaire vous tient au courant de l'avancement du dossier qui vous concerne, par écrit, pour conserver une trace." },
      { title: 'Demander une pièce', description: "Sans certaines pièces, l'acte ne peut être finalisé. Le courrier liste précisément ce qui est attendu." },
      { title: 'Annoncer un acte', description: "Avant chaque signature, le notaire vous transmet le projet d'acte pour relecture et validation." },
      { title: 'Préciser un délai', description: "Six mois pour une déclaration de succession, dix jours pour une rétractation : le courrier fixe les échéances clés." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient un courrier notarial\u00A0?",
    intro: "Quelle que soit sa forme, un courrier notarial obéit à une structure récurrente que voici.",
    cards: [
      { Icon: Stamp, title: "En-tête de l'étude", description: "Nom du notaire, étude, adresse, téléphone, numéro de dossier interne et nom du collaborateur en charge." },
      { Icon: Calendar, title: 'Date et référence', description: "Date d'envoi et référence du dossier — à citer dans toute réponse pour gagner du temps." },
      { Icon: Users, title: 'Destinataire', description: "Identité complète du destinataire (vous), parfois avec mention de votre qualité dans le dossier (héritier, vendeur)." },
      { Icon: MessageSquare, title: "Objet précis", description: "Sujet du courrier : demande de pièces, projet d'acte, convocation, relance, transmission d'éléments." },
      { Icon: FileText, title: 'Demande explicite', description: "Ce que le notaire attend de vous, formulé clairement : pièce à fournir, document à relire, rendez-vous à prendre." },
      { Icon: AlertCircle, title: 'Délai à respecter', description: "Date butoir avant laquelle vous devez agir. Tout retard peut avoir des conséquences fiscales ou juridiques." },
      { Icon: Inbox, title: 'Pièces jointes', description: "Liste des documents annexés au courrier : projet d'acte, attestation, formulaire à signer, devis." },
      { Icon: Mail, title: 'Coordonnées de réponse', description: "Manière de répondre au notaire : email, téléphone, courrier, dépôt à l'étude." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "Une lettre de notaire est rarement anodine. Quelques réflexes simples permettent de bien réagir.",
    items: [
      "Identifier l'objet exact du courrier : ce que le notaire vous demande précisément.",
      "Repérer la date limite : tout retard peut entraîner des pénalités ou bloquer un acte.",
      "Lister les pièces à fournir et préparer les originaux ou copies certifiées.",
      "Relire attentivement tout projet d'acte joint et noter vos questions ou remarques.",
      "Conserver une copie de toute réponse adressée à l'étude.",
      "Ne jamais ignorer une relance : appeler l'étude pour expliquer un éventuel retard.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import du courrier, HÉRITA en extrait l'essentiel pour vous faire gagner du temps.",
    items: [
      "Étude et notaire concernés",
      "Référence et date du dossier",
      "Objet exact du courrier",
      "Pièces à fournir précisément",
      "Délai à respecter",
      "Acte ou démarche en cours",
      "Risques en cas de non-réponse",
      'Questions à poser au notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif de courrier notarial. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Madame, Monsieur,<br /><br />
        Dans le cadre du règlement de la succession de Monsieur Pierre
        DURAND, votre père, je vous prie de bien vouloir me faire parvenir,
        dans un délai de quinze jours, les pièces suivantes&nbsp;: photocopie
        recto-verso de votre pièce d'identité en cours de validité, extrait
        d'acte de naissance datant de moins de trois mois, attestation de
        votre dernier domicile, RIB pour le versement éventuel des fonds.
        <br /><br />
        Sans réception de ces pièces dans ce délai, le dépôt de la
        déclaration de succession sera décalé et des intérêts de retard
        pourraient être réclamés par l'administration fiscale.<br /><br />
        Bien à vous,<br />
        Maître Claire MORIN&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour vous",
    bullets: [
      { strong: 'Le notaire prépare', rest: ' la déclaration de succession de votre père Pierre DURAND.' },
      { strong: 'Quatre pièces sont à fournir', rest: ' : pièce d\'identité, acte de naissance récent, justificatif de domicile et RIB.' },
      { strong: 'Délai impératif', rest: ' : 15 jours à compter de la réception. Idéalement à envoyer en recommandé.' },
      { strong: 'Conséquence en cas de retard', rest: ' : décalage du dépôt et intérêts de retard fiscaux à payer.' },
      { strong: 'Action recommandée', rest: ' : préparer les pièces et les transmettre rapidement à Maître Claire MORIN.' },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'un courrier notarial ?", a: "C'est une lettre — papier ou recommandée — émanant d'un notaire dans le cadre d'un dossier en cours (succession, vente, donation, divorce). Elle vous informe, vous demande une pièce, vous transmet un projet d'acte ou vous convoque." },
    { q: "Faut-il toujours répondre ?", a: "Oui, dans la grande majorité des cas. Une absence de réponse peut bloquer le dossier et entraîner des conséquences fiscales ou juridiques. Même pour signaler un retard, mieux vaut appeler ou écrire à l'étude." },
    { q: "Quel est le délai habituel ?", a: "Il est précisé dans le courrier lui-même. Pour les demandes de pièces, comptez 8 à 15 jours en général. Pour la signature d'un acte, le notaire propose un rendez-vous précis. Pour une déclaration de succession, le délai légal global est de 6 mois après le décès." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA identifie l'étude, l'objet du courrier, les pièces demandées, le délai à respecter et l'acte ou la démarche en cours. Vous obtenez en quelques minutes un résumé clair et la liste exacte des actions à entreprendre." },
    { q: "Que faire si je ne comprends pas le courrier ?", a: "Appelez l'étude. Les notaires sont tenus à un devoir de conseil et peuvent reformuler leur demande. Vous pouvez aussi importer la lettre dans HÉRITA pour obtenir une lecture claire en français courant." },
    { q: "Peut-on demander un délai supplémentaire ?", a: "Oui, dans la plupart des cas, à condition de prévenir l'étude par écrit avant l'expiration du délai initial. Le notaire est généralement compréhensif si la demande est justifiée par une absence, une maladie ou une difficulté à obtenir une pièce." },
    { q: "Comment envoyer ma réponse ?", a: "Selon ce que demande le notaire : par email pour des scans, par courrier simple pour des copies, par lettre recommandée pour des documents importants. Toujours mentionner la référence du dossier rappelée en haut du courrier." },
    { q: "Que faire en cas de relance ?", a: "Ne jamais ignorer. Contactez l'étude immédiatement pour expliquer le retard et convenir d'un nouveau délai. Une relance reste souvent sans conséquence si vous régularisez rapidement." },
    { q: "Est-ce que tous les notaires écrivent par courrier ?", a: "De plus en plus utilisent l'email pour les échanges informels, mais le courrier postal — voire le recommandé — reste la norme pour tout acte ou demande engageante. Le format dépend de la nature de l'information." },
    { q: "Quel rapport avec un acte notarié ?", a: "Le courrier précède ou suit toujours un acte. Demande de pièces avant rédaction, projet d'acte avant signature, copie de l'acte signé après finalisation. Un acte ne se signe jamais sans plusieurs échanges écrits préalables." },
  ],

  finalCta: {
    title: "Vous avez reçu un courrier notarial\u00A0?",
    subtitle: "Déposez la lettre. HÉRITA identifie automatiquement l'objet du courrier, les pièces demandées, le délai à respecter et la démarche en cours afin de vous fournir une lecture claire et personnalisée.",
  },
};

const CourrierNotarial = () => <GuidePage config={config} />;
export default CourrierNotarial;
